import { useEffect, useRef } from "react";

/**
 * Cyan "Matrix rain" using canvas. Lightweight & GPU-friendly.
 * Props:
 *  - density: 0..1 (default .9)
 *  - speed:   0..2 (default 1)
 *  - opacity: 0..1 overall opacity multiplier (default 1)
 */
export default function MatrixRain({ density = 0.9, speed = 1, opacity = 1 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true, willReadFrequently: false });

    const chars =
      "ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ0123456789<>/{}#$".split("");

    let columns = 0;
    let drops = [];
    const fontSize = 14;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(canvas.clientWidth * dpr);
      canvas.height = Math.floor(canvas.clientHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      columns = Math.floor(canvas.clientWidth / fontSize);
      drops = new Array(columns).fill(0).map(() =>
        Math.random() * (canvas.clientHeight / fontSize)
      );
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let last = performance.now();
    const targetFps = 30;
    const frameInterval = 1000 / targetFps;

    const draw = (now) => {
      const elapsed = now - last;
      if (elapsed < frameInterval) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      last = now - (elapsed % frameInterval);

      // Trailing fade for the rain
      ctx.fillStyle = `rgba(5, 5, 5, ${0.08 + (1 - density) * 0.05})`;
      ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);

      ctx.font = `${fontSize}px JetBrains Mono, monospace`;
      for (let i = 0; i < drops.length; i++) {
        const text = chars[Math.floor(Math.random() * chars.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        // Bright head
        ctx.fillStyle = `rgba(190, 240, 255, ${0.9 * opacity})`;
        ctx.fillText(text, x, y);

        // Cyan trail (one row above)
        ctx.fillStyle = `rgba(0, 207, 255, ${0.55 * opacity})`;
        ctx.fillText(text, x, y - fontSize);

        if (y > canvas.clientHeight && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i] += 1 * speed;
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [density, speed, opacity]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="matrix-rain-canvas"
      className="dncm-rain"
      style={{
        width: "100%",
        height: "100%",
        display: "block",
      }}
      aria-hidden="true"
    />
  );
}
